/**
 * BENGglas (LandVac vacuümglas) — catalogus + prijsengine, gedeeld door client en server.
 *
 * Bron: BENGglas_Productcatalogus.xlsx (assortiment/prijslijst, juli 2026) + de
 * "BENGglas Shaped panes"-brochure (vormtoeslagen). Alle prijzen excl. BTW, in eurocent.
 *
 * Prijsmodel: `costPerM2Cents` = INKOOPPRIJS uit de prijslijst. De verkoopprijs is
 * inkoop × (1 + marge%), waarbij de marge een admin-instelling is (settings/global
 * .bengMarginPct, default 0). Dezelfde marge geldt voor de binnenruit-opties en het
 * sample. De server (createOrder) herprijst autoritatief met dezelfde engine.
 */

// ── Assortiment ────────────────────────────────────────────────────────────────

export type BengLine =
  | "single-coated"
  | "double-coated"
  | "triple-coated"
  | "monu"
  | "monu-restover"
  | "solar-control"
  | "sun";

export type BengInnerPane = "2EVA" | "2PVB" | "akoestisch" | "hybride";
export type BengShape = "rechthoek" | "vorm" | "cirkel";

export interface BengLineInfo {
  label: string;
  description: string;
  /** Standaard-specs van de lijn (datasheet/prijslijst). */
  uValue: number;
  /** Lichttransmissie % — undefined = "aanpasbaar" (Sun). */
  lta?: number;
  /** Zontoetredingsfactor — undefined = "aanpasbaar" (Sun). */
  gValue?: number;
  /** Maximale ruit-afmetingen (mm) uit de prijslijst. */
  maxWmm: number;
  maxHmm: number;
  /** Hele lijn alleen op aanvraag (Sun). */
  quoteOnly?: boolean;
}

export const BENG_LINES: Record<BengLine, BengLineInfo> = {
  "single-coated": {
    label: "Single Coated",
    description: "Eén low-e coating — goede balans tussen licht en isolatie.",
    // Prijslijst april 2026: 8,3–16,3 mm = U 0,40; 6,7 mm heeft U 0,50 (zie sc-6.7). LTA 78 · g 0,58.
    uValue: 0.4, lta: 78, gValue: 0.58, maxWmm: 2350, maxHmm: 4150,
  },
  "double-coated": {
    label: "Double Coated",
    description: "Twee low-e coatings — uitstekende warmtewering.",
    uValue: 0.4, lta: 70, gValue: 0.39, maxWmm: 2350, maxHmm: 4150,
  },
  "triple-coated": {
    label: "Triple Coated",
    description: "Drie coatings — de laagste U-waarde van het assortiment.",
    uValue: 0.35, lta: 68, gValue: 0.33, maxWmm: 2350, maxHmm: 4150,
  },
  monu: {
    label: "Monu",
    description: "Extra helder monumentenglas — maximale lichtdoorval voor erfgoed.",
    uValue: 0.5, lta: 83, gValue: 0.68, maxWmm: 2350, maxHmm: 4150,
  },
  "monu-restover": {
    label: "Monu Restover",
    description: "Monumentenglas met authentieke uitstraling van oud vensterglas.",
    uValue: 0.5, lta: 75, gValue: 0.6, maxWmm: 1500, maxHmm: 2500,
  },
  "solar-control": {
    label: "Solar Control",
    description: "Maximale zonwering met een zeer lage g-waarde.",
    uValue: 0.5, lta: 45, gValue: 0.29, maxWmm: 2350, maxHmm: 4150,
  },
  sun: {
    label: "Sun",
    description: "Lichtdoorval en warmtewering op maat afgestemd — per project.",
    uValue: 0.5, maxWmm: 1500, maxHmm: 2500, quoteOnly: true,
  },
};

export interface BengProduct {
  /** Stabiel id, bv. "sc-8.3" — gaat mee in cart/order. */
  id: string;
  line: BengLine;
  thicknessMm: number;
  /** Inkoopprijs per m² excl. BTW in eurocent (0 = op aanvraag). */
  costPerM2Cents: number;
  /** Alleen op aanvraag (geen directe prijs). */
  quoteOnly?: boolean;
  /** Specs-afwijking t.o.v. de lijn (alleen 6,7 mm Single Coated). */
  specs?: { uValue: number; lta: number; gValue: number };
  /** Maat-afwijking t.o.v. de lijn (6,7 mm: max 1000 × 1500). */
  maxWmm?: number;
  maxHmm?: number;
}

/** Alle producten — exact de rijen uit BENGglas_Productcatalogus.xlsx. */
export const BENG_PRODUCTS: BengProduct[] = [
  // Single Coated — 6,7 mm: afwijkende specs (U 0,40 · LTA 70 · g 0,39), HALF gehard
  // (één glasblad gehard) en kleinere maximale maat (1000 × 1500 mm).
  { id: "sc-6.7",  line: "single-coated", thicknessMm: 6.7,  costPerM2Cents: 26000, specs: { uValue: 0.5, lta: 78, gValue: 0.58 }, maxWmm: 1000, maxHmm: 1500 },
  { id: "sc-8.3",  line: "single-coated", thicknessMm: 8.3,  costPerM2Cents: 24000 },
  { id: "sc-10.3", line: "single-coated", thicknessMm: 10.3, costPerM2Cents: 24000 },
  { id: "sc-12.3", line: "single-coated", thicknessMm: 12.3, costPerM2Cents: 25100 },
  { id: "sc-16.3", line: "single-coated", thicknessMm: 16.3, costPerM2Cents: 41100 },
  // Double Coated
  { id: "dc-8.3",  line: "double-coated", thicknessMm: 8.3,  costPerM2Cents: 24000 },
  { id: "dc-10.3", line: "double-coated", thicknessMm: 10.3, costPerM2Cents: 24000 },
  { id: "dc-12.3", line: "double-coated", thicknessMm: 12.3, costPerM2Cents: 25100 },
  { id: "dc-16.3", line: "double-coated", thicknessMm: 16.3, costPerM2Cents: 41100 },
  // Triple Coated
  { id: "tc-8.3",  line: "triple-coated", thicknessMm: 8.3,  costPerM2Cents: 29000 },
  { id: "tc-10.3", line: "triple-coated", thicknessMm: 10.3, costPerM2Cents: 29000 },
  { id: "tc-12.3", line: "triple-coated", thicknessMm: 12.3, costPerM2Cents: 30000 },
  { id: "tc-16.3", line: "triple-coated", thicknessMm: 16.3, costPerM2Cents: 49700 },
  // Monu (extra helder — vervangt de vroegere "Extra Clear")
  { id: "monu-8.3",  line: "monu", thicknessMm: 8.3,  costPerM2Cents: 26100 },
  { id: "monu-10.3", line: "monu", thicknessMm: 10.3, costPerM2Cents: 26100 },
  { id: "monu-12.3", line: "monu", thicknessMm: 12.3, costPerM2Cents: 27100 },
  { id: "monu-16.3", line: "monu", thicknessMm: 16.3, costPerM2Cents: 44700 },
  // Monu Restover — directe prijs, kleinere maxmaat (1500×2500)
  { id: "restover-8.3",  line: "monu-restover", thicknessMm: 8.3,  costPerM2Cents: 50000 },
  { id: "restover-10.3", line: "monu-restover", thicknessMm: 10.3, costPerM2Cents: 50000 },
  // Solar Control — géén 8,3 mm
  { id: "solar-10.3", line: "solar-control", thicknessMm: 10.3, costPerM2Cents: 26100 },
  { id: "solar-12.3", line: "solar-control", thicknessMm: 12.3, costPerM2Cents: 27100 },
  { id: "solar-16.3", line: "solar-control", thicknessMm: 16.3, costPerM2Cents: 44700 },
  // Sun — 14 mm, prijs op aanvraag
  { id: "sun-14", line: "sun", thicknessMm: 14, costPerM2Cents: 0, quoteOnly: true },
];

/** Oude product-ids (winkelwagens van vóór de xlsx-migratie) → nieuwe ids. */
const BENG_LEGACY_IDS: Record<string, string> = {
  "6.7-SC": "sc-6.7",
  "8.3-EC": "monu-8.3", "8.3-SC": "sc-8.3", "8.3-DC": "dc-8.3", "8.3-TC": "tc-8.3",
  "12.3-EC": "monu-12.3", "12.3-SC": "sc-12.3", "12.3-DC": "dc-12.3", "12.3-TC": "tc-12.3",
  "16.3-EC": "monu-16.3", "16.3-SC": "sc-16.3", "16.3-DC": "dc-16.3", "16.3-TC": "tc-16.3",
  monu: "monu-8.3",
  "monu-restover-8.3": "restover-8.3", "monu-restover-10.3": "restover-10.3",
  "solar-control": "solar-10.3",
  sun: "sun-14",
};

export function getBengProduct(id: string): BengProduct | undefined {
  const key = BENG_LEGACY_IDS[id] ?? id;
  return BENG_PRODUCTS.find((p) => p.id === key);
}

/** Producten van een lijn, dun → dik. */
export function bengProductsForLine(line: BengLine): BengProduct[] {
  return BENG_PRODUCTS.filter((p) => p.line === line).sort((a, b) => a.thicknessMm - b.thicknessMm);
}

/** Effectieve specs van een product (lijn-specs + eventuele 6,7-override). */
export function bengSpecsFor(product: BengProduct): { uValue: number; lta?: number; gValue?: number } {
  const line = BENG_LINES[product.line];
  return product.specs ?? { uValue: line.uValue, lta: line.lta, gValue: line.gValue };
}

/** Maximale ruit-afmeting (mm) van een product: eigen override (6,7 mm) óf de lijn-grens. */
export function bengMaxSize(product: BengProduct): { maxWmm: number; maxHmm: number } {
  const line = BENG_LINES[product.line];
  return { maxWmm: product.maxWmm ?? line.maxWmm, maxHmm: product.maxHmm ?? line.maxHmm };
}

/** 6,7 mm is HALF gehard (één glasblad gehard) i.p.v. volledig gehard. */
export function bengHalfTempered(product: BengProduct): boolean {
  return product.thicknessMm === 6.7;
}

/** Grens (mm) waarboven het speciale vacuümglas-transport niet meer standaard geprijsd
 *  kan worden → transport op aanvraag. Geldt als één zijde deze maat overschrijdt. */
export const BENG_TRANSPORT_MAX_SIDE_MM = 2500;
export function bengTransportOnRequest(widthMm: number, heightMm: number): boolean {
  return Math.max(widthMm, heightMm) > BENG_TRANSPORT_MAX_SIDE_MM;
}

// ── Opties & constanten ────────────────────────────────────────────────────────

/**
 * Binnenruit-opties — het vacuümglas krijgt een extra TEGENRUIT (standaard 4 mm gehard
 * helder, in de prijs inbegrepen):
 * - gelaagd (2EVA/2PVB/akoestisch): tegenruit met folie DIRECT op het vacuümglas verlijmd
 *   (geen spouw) — dunste samengestelde opbouw, vanaf ±13,1 mm (bron: bengglas.nl/gelaagd-glas).
 * - hybride: tegenruit op een SPOUW met afstandhouder — standaard of warm edge, zie BENG_SPACERS
 *   (een echt isolatiepakket) — beste isolatie/geluid (bron: bengglas.nl BENGglas Hybrid).
 * costPerM2Cents = €/m² INKOOP (BENGglas-prijslijst, incl. standaard tegenruit); zelfde marge
 * als het glas. foilMm = foliedikte: 1 folie = 0,38 mm, dus 2 folies = 0,76 mm; rwDb = indicatieve demping.
 */
export const BENG_INNER_PANE: Record<BengInnerPane, { label: string; costPerM2Cents: number; description: string; foilMm: number; rwDb: string }> = {
  "2EVA":       { label: "Gelaagd — 2× EVA-folie", costPerM2Cents: 4600, foilMm: 0.76, rwDb: "39", description: "Veelzijdig en zeer goed bestand tegen vocht — voor de meeste toepassingen." },
  "2PVB":       { label: "Gelaagd — 2× PVB-folie", costPerM2Cents: 7000, foilMm: 0.76, rwDb: "39", description: "Industriestandaard (NEN) — aanbevolen voor daklichten en buiten." },
  "akoestisch": { label: "Gelaagd — PVB SoundControl", costPerM2Cents: 8000, foilMm: 0.76, rwDb: "41", description: "Akoestische folie — maximale geluidsdemping langs drukke wegen." },
  hybride:      { label: "Hybride — tegenruit op spouw", costPerM2Cents: 5100, foilMm: 0, rwDb: "39", description: "Spouw met afstandhouder + geharde tegenruit (gelijk aan het glasblad)." },
};

/**
 * Afstandhouder in de hybride spouw — standaard of thermisch onderbroken ("warm edge").
 *
 * ALLEEN SPEC, GEEN PRIJSINVLOED: de BENGglas-prijslijst kent geen spouw-/afstandhouderregel
 * (zie docs/BENGLAS-PRIJSREFERENTIE.md), beide uitvoeringen vallen onder hetzelfde hybride-
 * tarief van €51/m². Krijgt BENG hier later wél een toeslag voor, dan is dit de plek: een
 * `costPerM2Cents` per uitvoering erbij en meenemen in `bengInnerPaneCostCents`.
 * Kleuren gelijk aan de isolatieglas-afstandhouders (alu grijs / TGI zwart), zodat de
 * doorsnede-tekeningen in beide configurators dezelfde taal spreken.
 */
export type BengSpacer = "standaard" | "warm-edge";

export const BENG_SPACERS: Record<BengSpacer, { label: string; kort: string; uitleg: string; hex: string }> = {
  standaard: {
    label: "Standaard afstandhouder",
    kort: "standaard",
    uitleg: "De gebruikelijke aluminium afstandhouder tussen het vacuümglas en de tegenruit.",
    hex: "#c7ccd1",
  },
  "warm-edge": {
    label: "Warm edge-afstandhouder",
    kort: "warm edge",
    uitleg:
      "Thermisch onderbroken afstandhouder: minder warmteverlies langs de rand van de ruit en daardoor minder kans op condens op de randzone.",
    hex: "#3a3f47",
  },
};

/** Standaardkeuze wanneer er (nog) geen afstandhouder is gekozen — óók de terugval voor
 *  bestellingen van vóór 01-08-2026, toen de hybride spouw altijd warm edge was. Die oude
 *  orders dragen hun uitvoering in hun eigen opgeslagen regeltekst, dus die blijft kloppen. */
export const BENG_SPACER_DEFAULT: BengSpacer = "standaard";

/** Volledig label ("Warm edge-afstandhouder") van een afstandhouder-keuze. */
export function bengSpacerLabel(spacer?: BengSpacer | ""): string {
  return BENG_SPACERS[spacer || BENG_SPACER_DEFAULT].label;
}

/** Kort label ("warm edge") voor inline gebruik achter de spouwmaat. */
export function bengSpacerShort(spacer?: BengSpacer | ""): string {
  return BENG_SPACERS[spacer || BENG_SPACER_DEFAULT].kort;
}

/** Standaard-tegenruit (mm, gehard helder) bij de GELAAGDE (verlijmde) opbouw. */
export const BENG_TEGENRUIT_MM = 4;

/** Inkoop-meerkost (€/m², cents) van een binnenruit-opbouw t.o.v. de kale ruit.
 *  hybride            = geharde tegenruit op warm-edge spouw → €51/m² (BENGglas-prijslijst).
 *  hybride-gelaagd    = diezelfde hybride-opbouw maar met een GELAAGDE tegenruit (2× EVA-folie)
 *                       → €67/m² (eigen prijslijst-tarief, niet 51+46 opgeteld). */
export const BENG_HYBRID_LAMINATED_EVA_CENTS = 6700;
export function bengInnerPaneCostCents(kind: BengCompositionKind): number {
  if (kind === "hybride-gelaagd") return BENG_HYBRID_LAMINATED_EVA_CENTS;
  return BENG_INNER_PANE[kind].costPerM2Cents;
}

/** Label van een binnenruit-opbouw voor op de order/winkelwagen. */
export function bengInnerPaneLabel(kind: BengCompositionKind): string {
  return kind === "hybride-gelaagd" ? "Hybride gelaagd — gelaagde tegenruit (2× EVA) op spouw" : BENG_INNER_PANE[kind].label;
}

/** Geluidsdemping Rw (C;Ctr) in dB van de kale vacuümruit (datasheets). */
export const BENG_RW_DB = "36 (-2;-3)";

/** Indicatief gewicht (kg/m²): (totale dikte − 0,3mm vacuüm) × 2,5 kg/m²/mm. */
export function bengWeightKgM2(thicknessMm: number): number {
  return Math.round((thicknessMm - 0.3) * 2.5 * 10) / 10;
}

/** Glasblad-dikte (mm) van één blad van het vacuümglas: (totaal − 0,3 vacuüm) / 2.
 *  Bepaalt óók de hybride tegenruit: die is gelijk aan het glasblad (8,3 → 4 mm gehard). */
export function bengBladeMm(baseMm: number): number {
  return Math.round(((baseMm - 0.3) / 2) * 10) / 10;
}

/** Hybride uitvoerbaar? Niet voor 6,7 mm (half gehard glasblad). */
export function bengHybridAvailable(baseMm: number): boolean {
  return baseMm >= 8;
}

/** Gelaagde tegenruit (2× EVA-folie) bij "hybride gelaagd", bladen gelijk aan het
 *  vacuümglas — 8,3 → 4+4 · 10,3 → 5+5 · 12,3 → 6+6 mm. Andere diktes: niet leverbaar. */
export function bengHybridLaminated(baseMm: number): { label: string; glassMm: number; totalMm: number } | null {
  const blade = bengBladeMm(baseMm);
  if (blade === 4) return { label: "gelaagd 2× EVA (4+4 mm)", glassMm: 8, totalMm: 8.8 };
  if (blade === 5) return { label: "gelaagd 2× EVA (5+5 mm)", glassMm: 10, totalMm: 10.8 };
  if (blade === 6) return { label: "gelaagd 2× EVA (6+6 mm)", glassMm: 12, totalMm: 12.8 };
  return null;
}

/** Opbouw-soorten: de geprijsde binnenruit-opties + "hybride-gelaagd" (op aanvraag). */
export type BengCompositionKind = BengInnerPane | "hybride-gelaagd";

/** Samengestelde opbouw: totale dikte + gewicht + demping van basisruit (+ binnenruit).
 *  - gelaagd (2EVA/2PVB/akoestisch): + folie + standaard tegenruit 4 mm (verlijmd, geen spouw)
 *  - hybride: + spouw + geharde tegenruit gelijk aan het glasblad (8,3 → 4 mm)
 *  - hybride-gelaagd: + spouw + gelaagde tegenruit (44.2/55.2/66.2) */
export function bengComposition(baseMm: number, kind?: BengCompositionKind | "", spouwMm?: number): {
  totalMm: number;
  weightKgM2: number;
  rwDb: string;
  /** Omschrijving van de tegenruit (leeg bij kale ruit). */
  tegenruit: string;
} {
  const baseW = bengWeightKgM2(baseMm);
  if (!kind) return { totalMm: baseMm, weightKgM2: baseW, rwDb: BENG_RW_DB, tegenruit: "" };
  if (kind === "hybride") {
    const t = bengBladeMm(baseMm);
    return {
      totalMm: Math.round((baseMm + (spouwMm ?? 0) + t) * 10) / 10,
      weightKgM2: Math.round((baseW + t * 2.5) * 10) / 10,
      rwDb: "39",
      tegenruit: `${`${t}`.replace(".", ",")} mm gehard`,
    };
  }
  if (kind === "hybride-gelaagd") {
    const lam = bengHybridLaminated(baseMm);
    if (!lam) return { totalMm: baseMm, weightKgM2: baseW, rwDb: BENG_RW_DB, tegenruit: "" };
    return {
      totalMm: Math.round((baseMm + (spouwMm ?? 0) + lam.totalMm) * 10) / 10,
      weightKgM2: Math.round((baseW + lam.glassMm * 2.5) * 10) / 10,
      rwDb: "41",
      tegenruit: lam.label,
    };
  }
  // Gelaagd (verlijmd): folie + standaard geharde tegenruit, geen spouw. De tegenruit is gelijk
  // aan het glasblad van het vacuümglas (schaalt mee met de dikte: 8,3 → 4 mm, 16,3 → 8 mm).
  const opt = BENG_INNER_PANE[kind];
  const teg = bengBladeMm(baseMm);
  return {
    totalMm: Math.round((baseMm + opt.foilMm + teg) * 10) / 10,
    weightKgM2: Math.round((baseW + teg * 2.5) * 10) / 10,
    rwDb: opt.rwDb,
    tegenruit: `${`${teg}`.replace(".", ",")} mm gehard`,
  };
}

/** Minimale ruit-afmetingen (mm). */
export const BENG_MIN_W = 200;
export const BENG_MIN_H = 230;
/** Minimale afrekenoppervlakte (m²) — prijslijst: 0,3 voor alle lijnen. */
export const BENG_MIN_AREA_M2 = 0.3;

/** Vormtoeslag (brochure "Shaped panes"): vormruit +50%, volledige cirkel +200%. */
export const BENG_SHAPE_SURCHARGE: Record<BengShape, number> = {
  rechthoek: 0,
  vorm: 0.5,
  cirkel: 2.0,
};

/** Sample-prijs (excl. BTW) in cents — INKOOP; zelfde marge als het glas. */
export const BENG_SAMPLE_CENTS = 6200;

/** Verzendbijdrage (excl. BTW) in cents — eenmalig per order (zie ook ShippingConfig). */
export const BENG_SHIPPING_NL = 19500;
export const BENG_SHIPPING_BE = 22500;

/** BTW-tarief (zelfde 21% als de rest van de engine; lokaal om circulaire imports te mijden). */
const BENG_VAT_RATE = 0.21;

// ── Prijsengine ────────────────────────────────────────────────────────────────

export interface BengConfigInput {
  productId: string;
  widthMm: number;
  heightMm: number;
  quantity: number;
  shape?: BengShape;
  /** Binnenruit-opbouw (gelaagd 2EVA/2PVB/akoestisch, hybride of hybride-gelaagd). */
  innerPane?: BengCompositionKind;
  /** Spouwmaat (mm) tussen basisruit en hybride tegenruit. Alleen spec — géén prijsinvloed. */
  spouwMm?: number;
  /** Afstandhouder in die spouw (standaard/warm edge). Ook alleen spec — géén prijsinvloed. */
  spacer?: BengSpacer;
}

export interface BengPriceBreakdown {
  product: BengProduct;
  areaM2: number;
  billableAreaM2: number;
  /** Toegepaste marge (%) over de inkoopprijzen. */
  marginPct: number;
  /** Verkoopprijs glas per m² (incl. marge), excl. BTW. */
  sellPerM2Cents: number;
  /** Toeslag voor oversized panelen (factor, 1 = geen toeslag). */
  oversizedFactor: number;
  oversizedLabel: string;
  /** Toeslag voor niet-rechthoekige vormen (factor over glasprijs). */
  shapeSurchargePct: number;
  /** Glasprijs per ruit excl. BTW, in cents. */
  glasPriceCents: number;
  /** Prijs binnenruit-optie per ruit excl. BTW, in cents (0 als geen). */
  innerPaneCents: number;
  /** Stukprijs excl. BTW, in cents. */
  unitPriceCents: number;
  quantity: number;
  /** Subtotaal excl. BTW (unitPrice × quantity), in cents. */
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  /** Leesbare spec-omschrijving voor in de winkelwagen/order. */
  displayLines: string[];
}

export class BengPricingError extends Error {
  constructor(public readonly errors: string[]) {
    super(errors.join(" "));
    this.name = "BengPricingError";
  }
}

/** Verkoopprijs in cents: inkoop × (1 + marge%), afgerond op hele centen. */
export function bengSellCents(costCents: number, marginPct: number): number {
  const pct = Number.isFinite(marginPct) && marginPct > 0 ? marginPct : 0;
  return Math.round(costCents * (1 + pct / 100));
}

/**
 * Oversized-toeslag op basis van afmetingen (mm). Geldt NIET voor 16,3 mm.
 * ×1,3: lange zijde 2500–3000 óf korte zijde 1500–2000 · ×1,8: lange zijde >3000 óf korte >2000.
 */
function oversized(widthMm: number, heightMm: number, thicknessMm: number): { factor: number; label: string } {
  if (thicknessMm >= 16) return { factor: 1, label: "" };
  const long = Math.max(widthMm, heightMm);
  const short = Math.min(widthMm, heightMm);
  if (long > 3000 || short > 2000) return { factor: 1.8, label: "Zeer groot formaat (×1,8)" };
  if (long > 2500 || short > 1500) return { factor: 1.3, label: "Groot formaat (×1,3)" };
  return { factor: 1, label: "" };
}

export function validateBengConfig(input: BengConfigInput): string[] {
  const { productId, widthMm, heightMm, quantity } = input;
  const errors: string[] = [];

  const product = getBengProduct(productId);
  if (!product) {
    errors.push("Ongeldig product geselecteerd.");
    return errors;
  }
  const line = BENG_LINES[product.line];
  const { maxWmm, maxHmm } = bengMaxSize(product);
  const dikte = `${product.thicknessMm}`.replace(".", ",");

  if (!Number.isFinite(widthMm) || widthMm < BENG_MIN_W) {
    errors.push(`Breedte moet minimaal ${BENG_MIN_W} mm zijn.`);
  }
  if (!Number.isFinite(heightMm) || heightMm < BENG_MIN_H) {
    errors.push(`Hoogte moet minimaal ${BENG_MIN_H} mm zijn.`);
  }
  if (Number.isFinite(widthMm) && widthMm > maxWmm) {
    errors.push(`Breedte mag maximaal ${maxWmm} mm zijn voor ${line.label} ${dikte} mm.`);
  }
  if (Number.isFinite(heightMm) && heightMm > maxHmm) {
    errors.push(`Hoogte mag maximaal ${maxHmm} mm zijn voor ${line.label} ${dikte} mm.`);
  }
  if (!Number.isInteger(quantity) || quantity < 1) {
    errors.push("Aantal moet een heel getal van minimaal 1 zijn.");
  }

  return errors;
}

/** Prijsopbouw van een BENGglas-ruit. marginPct = admin-marge (%) over de inkoopprijzen. */
export function calculateBengPrice(input: BengConfigInput, marginPct = 0): BengPriceBreakdown {
  const errors = validateBengConfig(input);
  if (errors.length > 0) throw new BengPricingError(errors);

  const product = getBengProduct(input.productId)!;
  if (product.quoteOnly || product.costPerM2Cents <= 0) {
    throw new BengPricingError(["Dit product is alleen op aanvraag — vraag een offerte aan."]);
  }
  const { widthMm, heightMm, quantity, shape = "rechthoek", innerPane } = input;

  const areaM2 = (widthMm / 1000) * (heightMm / 1000);
  const billableAreaM2 = Math.max(areaM2, BENG_MIN_AREA_M2);

  const { factor: oversizedFactor, label: oversizedLabel } = oversized(widthMm, heightMm, product.thicknessMm);
  const shapeSurchargePct = BENG_SHAPE_SURCHARGE[shape];
  const sellPerM2Cents = bengSellCents(product.costPerM2Cents, marginPct);

  // Glasprijs: billable area × verkoopprijs/m² × oversized × (1 + vormtoeslag)
  const glasBase = billableAreaM2 * sellPerM2Cents;
  const glasWithOversized = glasBase * oversizedFactor;
  const glasPriceCents = Math.round(glasWithOversized * (1 + shapeSurchargePct));

  // Binnenruit-optie: óók de vormtoeslag telt hierover (de vormtoeslag geldt over de héle
  // samenstelling — basisglas + gelaagde/hybride binnenruit), naast oversized + marge.
  const innerPaneCents = innerPane
    ? Math.round(billableAreaM2 * bengSellCents(bengInnerPaneCostCents(innerPane), marginPct) * oversizedFactor * (1 + shapeSurchargePct))
    : 0;

  const unitPriceCents = glasPriceCents + innerPaneCents;
  const subtotalCents = unitPriceCents * quantity;
  const vatCents = Math.round(subtotalCents * BENG_VAT_RATE);
  const totalCents = subtotalCents + vatCents;

  const specs = bengSpecsFor(product);
  const displayLines: string[] = [
    `${widthMm} × ${heightMm} mm`,
    `${`${product.thicknessMm}`.replace(".", ",")} mm — ${BENG_LINES[product.line].label}`,
    `U-waarde ≈ ${specs.uValue.toFixed(2).replace(".", ",")} W/m²K`,
  ];
  if (innerPane) {
    // De afstandhouder hoort bij de spouw, dus alleen tonen als er een spouw ís (hybride).
    const spouwSpec = input.spouwMm ? ` · spouw ${input.spouwMm} mm (${bengSpacerShort(input.spacer)})` : "";
    displayLines.push(bengInnerPaneLabel(innerPane) + spouwSpec);
  }
  if (shape !== "rechthoek") displayLines.push(shape === "cirkel" ? "Cirkel (+200%)" : "Vormruit (+50%)");
  if (oversizedLabel) displayLines.push(oversizedLabel);

  return {
    product,
    areaM2,
    billableAreaM2,
    marginPct,
    sellPerM2Cents,
    oversizedFactor,
    oversizedLabel,
    shapeSurchargePct,
    glasPriceCents,
    innerPaneCents,
    unitPriceCents,
    quantity,
    subtotalCents,
    vatCents,
    totalCents,
    displayLines,
  };
}
